import React, { useContext, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { LotContext } from '../Lot/LotContext'
import { Empty } from '../../components'
import { WemoloButton, WemoloLoading, WemoloRefreshIcon } from '../../components/Wemolo'
import { ActionBarContainer } from './ActionBar'
import {
    buildRequestFromChips,
    hasIncompleteDetectionId,
    useDetectionExplorerStore,
    ViewMode
} from './DetectionExplorerStore'
import { FiltersContainer } from './Filters'
import { GalleryContainer } from './Gallery'
import { GroupByLPContainer } from './GroupByLP'
import { ImageRequestsPanel } from './ImageRequests'
import { ResultFiltersContainer } from './ResultFilters'
import { TableContainer } from './Table'
import { anyClientFilterActive } from './detectionFilters'
import { clampToWindow, computeWindowBounds } from './timeWindow'
import { DetectionResult, useGetDetectionsQuery } from './get_detections'
import { useFilteredDetections } from './useFilteredDetections'
import { useListImageRequestsQuery } from './list_image_requests'
import {
    CardSize,
    DETECTION_EXPLORER_PAGE_SIZE,
    DETECTION_EXPLORER_IMAGE_REQUESTS_POLL_MS,
    DETECTION_EXPLORER_TEXT
} from './config'
import styles from './DetectionExplorer.module.css'

const CARD_SIZE_CLASS: Record<CardSize, string> = {
    small: styles.sizeSmall,
    medium: styles.sizeMedium,
    large: styles.sizeLarge
}

const renderView = (viewMode: ViewMode, items: DetectionResult[]) => {
    switch (viewMode) {
        case ViewMode.GalleryView:
            return <GalleryContainer items={items} />
        case ViewMode.GroupByLPView:
            return <GroupByLPContainer items={items} />
        case ViewMode.ListView:
        default:
            return <TableContainer items={items} />
    }
}

export const DetectionExplorerContainer = () => {
    const viewMode = useDetectionExplorerStore((s) => s.viewMode)
    const filters = useDetectionExplorerStore((s) => s.filters)
    const activeFilters = useDetectionExplorerStore((s) => s.activeFilters)
    const setBaseFilters = useDetectionExplorerStore((s) => s.setBaseFilters)
    const resetForLot = useDetectionExplorerStore((s) => s.resetForLot)
    const imageRequestsOpen = useDetectionExplorerStore((s) => s.imageRequestsOpen)
    const toggleImageRequests = useDetectionExplorerStore((s) => s.toggleImageRequests)
    const applyImageRequest = useDetectionExplorerStore((s) => s.applyImageRequest)
    const appliedImageRequestId = useDetectionExplorerStore((s) => s.appliedImageRequestId)
    const clientFilters = useDetectionExplorerStore((s) => s.clientFilters)
    const cardSize = useDetectionExplorerStore((s) => s.cardSize)
    const timePreset = useDetectionExplorerStore((s) => s.timePreset)
    const setTimePreset = useDetectionExplorerStore((s) => s.setTimePreset)
    const { imageRetrieval } = useFlags()
    const { selectedLot, lotStations } = useContext(LotContext)

    const lotId = selectedLot?.id ?? ''

    // A device can sit on more than one station, so dedupe — get_detections wants a unique set.
    const deviceUuidsKey = useMemo(
        () =>
            [
                ...new Set(
                    (lotStations?.station ?? [])
                        .map((s) => s.device_uuid)
                        .filter((uuid): uuid is string => Boolean(uuid))
                )
            ]
                .sort()
                .join(','),
        [lotStations]
    )

    // Reset chips, selection, page, and window whenever the lot changes (including initial mount).
    const lastResetLotRef = useRef<string>('')
    useEffect(() => {
        if (!lotId || !deviceUuidsKey || lastResetLotRef.current === lotId) return
        lastResetLotRef.current = lotId
        // computeWindowBounds keeps `to` a couple minutes in the past (clock-skew grace) and `from`
        // at the full 48h floor, matching the preset snapping so the initial load passes too.
        const bounds = computeWindowBounds()
        resetForLot({ lot: lotId, from: bounds.cutoff, to: bounds.now })
    }, [lotId, deviceUuidsKey, resetForLot])

    // Keep device_uuids in sync — stations can change without the lot changing.
    useEffect(() => {
        if (!deviceUuidsKey) return
        setBaseFilters({ device_uuids: deviceUuidsKey.split(',') })
    }, [deviceUuidsKey, setBaseFilters])

    const request = buildRequestFromChips({ ...filters, page_size: DETECTION_EXPLORER_PAGE_SIZE }, activeFilters)

    // A client-side result filter (image presence / LP-case content) can't be pushed to the backend,
    // so when one is active we switch to the accumulating path; otherwise the normal single-page
    // query runs. Exactly one is enabled at a time.
    const isGroupByLP = viewMode === ViewMode.GroupByLPView
    const filtering = anyClientFilterActive(clientFilters, isGroupByLP)
    const baseEnabled = Boolean(filters.lot) && filters.device_uuids.length > 0

    const exact = useGetDetectionsQuery(request, { enabled: baseEnabled && !filtering })
    const filtered = useFilteredDetections({
        request,
        clientFilters,
        isGroupByLP,
        page: filters.page,
        enabled: baseEnabled && filtering
    })

    const items = filtering ? filtered.items : (exact.data?.results ?? [])
    const total = exact.data?.total ?? 0
    const isFetching = filtering ? filtered.isLoadingMore : exact.isFetching
    const isError = filtering ? filtered.isError : exact.isError
    const error = filtering ? filtered.error : exact.error
    // Whether there's anything to render yet (first page resolved), gating the initial spinner.
    const ready = filtering ? !filtered.isInitialLoading : Boolean(exact.data)

    // The image-request list is its own resource — fetched independently so its statuses refresh
    // without refetching detections, and a re-query of detections doesn't re-pull the list. Polls
    // so an optimistic row (inserted on retrieve) reconciles and statuses advance over time.
    const {
        data: imageRequestsData,
        refetch: refetchImageRequests,
        isFetching: imageRequestsFetching
    } = useListImageRequestsQuery(lotId, {
        enabled: Boolean(imageRetrieval) && Boolean(lotId),
        refetchInterval: DETECTION_EXPLORER_IMAGE_REQUESTS_POLL_MS
    })

    const imageRequests = imageRequestsData?.image_requests ?? []

    // A half-typed Detection ID is dropped from the request (so it never 400s); surface an empty
    // state rather than silently showing unfiltered results until a complete UUID is entered.
    const incompleteDetectionId = hasIncompleteDetectionId(activeFilters)

    const noDevices = lotStations !== undefined && deviceUuidsKey === ''

    // Without a selected lot the query never runs, so show an empty state instead of a perpetual spinner.
    if (!selectedLot) {
        return <Empty title="Select a lot first!" />
    }

    // Re-snap the time window on demand (refresh button). A long-idle tab's `from` drifts past the
    // backend's 48h limit and the query 400s; re-applying the preset (or clamping a custom window)
    // pulls it back into range, which changes the request and re-runs the query.
    const handleRefresh = () => {
        if (timePreset === 'custom') {
            const bounds = computeWindowBounds()
            setBaseFilters({ from: clampToWindow(filters.from, bounds), to: clampToWindow(filters.to, bounds) })
        } else {
            setTimePreset(timePreset)
        }
    }

    // The backend rejects a `from` older than its 48h window — surface a friendly hint for that case.
    const isStaleWindowError = /within the last 48 hours|must be within/i.test(error?.message ?? '')

    // Pick the viewport content by priority — guard clauses keep this readable where a deeply
    // nested ternary wouldn't.
    const renderViewport = () => {
        if (isError) {
            return (
                <Empty
                    title={DETECTION_EXPLORER_TEXT.loadErrorTitle}
                    subtitle={isStaleWindowError ? DETECTION_EXPLORER_TEXT.loadErrorStaleWindow : error?.message}
                    action={
                        <WemoloButton view="primary" size="small" icon={<WemoloRefreshIcon />} onClick={handleRefresh}>
                            {DETECTION_EXPLORER_TEXT.refresh}
                        </WemoloButton>
                    }
                />
            )
        }
        if (incompleteDetectionId) {
            return (
                <Empty
                    title={DETECTION_EXPLORER_TEXT.incompleteDetectionIdTitle}
                    subtitle={DETECTION_EXPLORER_TEXT.incompleteDetectionIdHint}
                />
            )
        }
        if (noDevices) {
            return (
                <Empty
                    title={DETECTION_EXPLORER_TEXT.noDevicesTitle}
                    subtitle={DETECTION_EXPLORER_TEXT.noDevicesHint}
                />
            )
        }
        if (!ready) {
            return (
                <div className={styles.loading}>
                    <WemoloLoading />
                </div>
            )
        }
        return (
            <>
                <div className={styles.body}>
                    <div className={classNames(styles.scroll, CARD_SIZE_CLASS[cardSize])}>
                        {renderView(viewMode, items)}
                    </div>
                    {imageRetrieval && imageRequestsOpen && (
                        <ImageRequestsPanel
                            requests={imageRequests}
                            appliedRequestId={appliedImageRequestId}
                            onApply={applyImageRequest}
                            onClose={toggleImageRequests}
                            onRefresh={refetchImageRequests}
                            isRefreshing={imageRequestsFetching}
                        />
                    )}
                </div>
                {isFetching && (
                    <div className={styles.overlay}>
                        <WemoloLoading />
                    </div>
                )}
            </>
        )
    }

    return (
        <div className={styles.root}>
            <FiltersContainer />
            <ActionBarContainer
                items={items}
                total={total}
                filtering={filtering}
                scannedCount={filtered.scannedCount}
                serverTotal={filtered.serverTotal}
                hasMore={filtered.hasMore}
            />
            <ResultFiltersContainer />
            <div className={styles.viewport}>{renderViewport()}</div>
        </div>
    )
}
